import { QueryCache, QueryClient } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { toast } from "sonner";
import type { AppRouter } from "../../../server/dist/routers";
import { serverUrl } from "./server-url";

const dbErrorPhrases = [
  "Failed query",
  "insert into",
  "update set",
  "delete from",
] as const;

function getErrorMessage(error: Error): string {
  const isDatabaseError = dbErrorPhrases.some((phrase) =>
    error.message.includes(phrase)
  );
  return isDatabaseError
    ? "Something went wrong. Please try again or contact support if the problem persists."
    : error.message;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only show toast if query opts in via meta.showErrorToast
      if (!query.meta?.showErrorToast) {
        return;
      }

      toast.error(getErrorMessage(error), {
        action: {
          label: "retry",
          onClick: () => {
            queryClient.invalidateQueries();
          },
        },
      });
    },
  }),
});

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${serverUrl}/trpc`,
      fetch(url, options) {
        return fetch(url, {
          ...options,
          credentials: "include",
        });
      },
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});

// Wrap any tRPC queryOptions to show an error toast on failure
export function withErrorToast<T extends { meta?: Record<string, unknown> }>(
  queryOptions: T
): T {
  return {
    ...queryOptions,
    meta: { ...queryOptions.meta, showErrorToast: true },
  };
}
